import React, { useEffect, useState } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  TextField,
  Autocomplete
} from '@mui/material';
import { getEventDate } from '#component/device/overview/events/helper';
import { fetchProjectAlertConfirmations } from '#action/project/alert-confirmations';
import {
  patchShipmentAlertAcceptance,
  deleteShipmentAlertAcceptance
} from '#action/shipment/alert-acceptance';
import {
  patchApplianceAlertAcceptance,
  deleteApplianceAlertAcceptance
} from '#action/warehouse/appliance/alert-acceptance';
import {
  ContentWrapper,
  LoadingContentWrapper,
  AlertDialogContent,
  AlertFormControl,
  WithdrawConfirmation,
  ButtonContainer
} from './styles';
import CustomDialogTitle from './dialog-title';

export const AlertConfirmationDialog = ({
  isOpen,
  item,
  setConfirmationPopupData,
  syncSelectedDevicesWithStore
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [confirmationOption, setConfirmationOption] = useState(
    () => ({ value: item?.details?.acceptance?.value })
  );
  const [withdrawConfirmation, setWithdrawConfirmation] = useState(false);
  const account = useSelector(s => s.account, shallowEqual);
  const projectsAlertConfirmations = useSelector(s => s.projectAlertConfirmations, shallowEqual);
  const { appliance } = useSelector(s => s.appliance, shallowEqual) ?? {};
  const projectId = item?.details?.project?.id || appliance?.projectId;
  const applianceId = appliance?.id;
  const shipmentId = item?.details?.shipment?.id;
  const options = projectsAlertConfirmations[projectId] ?? [];
  const isEdit = Boolean(item?.details?.acceptance);
  const dialogTitle = isEdit ? 'Edit confirmation' : 'Confirm alert';
  const sameValue = item?.details?.acceptance?.value === confirmationOption?.value;
  const isDisabledSave = !withdrawConfirmation && (sameValue || !confirmationOption?.value);

  const DialogContentWrapper = loading ? LoadingContentWrapper : ContentWrapper;

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchProjectAlertConfirmations(projectId));
      setLoading(false);
    };

    if (projectId) {
      fetchData();
    }
  }, [dispatch, projectId]);

  const handleChange = (event, data) => {
    setConfirmationOption(data);
  };

  const handleInputChange = newInputValue => {
    setConfirmationOption({ value: newInputValue });
  };

  const handleClose = () => {
    setConfirmationPopupData(null);
  };

  const handleWithdrawConfirmationChange = e => {
    setWithdrawConfirmation(e.target.checked);
  };

  const handleDeleteAcceptance = async () => {
    let response;

    if (shipmentId) {
      response = await dispatch(deleteShipmentAlertAcceptance(shipmentId, item));
    }

    if (applianceId) {
      response = await dispatch(deleteApplianceAlertAcceptance(applianceId, item));
    }

    if (response.success) {
      syncSelectedDevicesWithStore?.(item);
      handleClose();
    }
  };

  const handleSaveAcceptance = async () => {
    let response;

    if (!confirmationOption?.value) {
      handleClose();
      return;
    }

    const { value } = confirmationOption;

    if (shipmentId) {
      response = await dispatch(patchShipmentAlertAcceptance(shipmentId, item, { value }));
    }

    if (applianceId) {
      response = await dispatch(
        patchApplianceAlertAcceptance(applianceId, item, { value })
      );
    }

    if (response.success) {
      syncSelectedDevicesWithStore?.(item, value);
      handleClose();
    }
  };

  const handleSave = () => {
    if (withdrawConfirmation) {
      handleDeleteAcceptance();
    } else {
      handleSaveAcceptance();
    }
  };

  const getFilterOptions = opts => {
    if (confirmationOption?.value) {
      return opts.filter(option =>
        option.value.toLowerCase().includes(confirmationOption.value.toLowerCase()));
    }

    return opts;
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="alert-confirmation-dialog-title"
      aria-describedby="alert-confirmation-dialog-description"
    >
      <CustomDialogTitle
        onClose={handleClose}
      >
        {dialogTitle}
      </CustomDialogTitle>
      <DialogContentWrapper>
        <AlertDialogContent>
          {getEventDate(item, account)}
        </AlertDialogContent>
        <div>
          <AlertFormControl variant="outlined">
            <Autocomplete
              value={confirmationOption}
              onChange={handleChange}
              onInputChange={(event, newInputValue) => {
                handleInputChange(newInputValue);
              }}
              options={options}
              filterOptions={o => getFilterOptions(o)}
              getOptionLabel={option => option.value ?? ''}
              isOptionEqualToValue={(option, currVal) => option.value === currVal.value}
              renderInput={params =>
                <TextField
                  {...params}
                  label="Options"
                  variant="outlined"
                  type="search"
                />}
            />
          </AlertFormControl>
        </div>
        {isEdit &&
          <WithdrawConfirmation>
            <FormControlLabel
              control={
                <Checkbox
                  checked={withdrawConfirmation}
                  onChange={handleWithdrawConfirmationChange}
                  name="withdrawConfirmation"
                />
              }
              label="WITHDRAW THE CONFIRMATION"
            />
          </WithdrawConfirmation>}
      </DialogContentWrapper>
      <DialogActions>
        <ButtonContainer>
          <Button
            variant="outlined"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            onClick={handleSave}
            disabled={isDisabledSave}
          >
            Save
          </Button>
        </ButtonContainer>
      </DialogActions>
    </Dialog>
  );
};
